import React, { useEffect } from "react";
import axios from "axios";
import "../styles/AppUCJ.css";
import { RegistrationProvider, useRegistration, useRegistrationDispatch } from "../context/RegistrationContext";
import AppHeader from "../components/presentational/AppHeader";
import Footer from "../components/presentational/Footer";
import Start from "../components/presentational/Start";
import StepTenantSelector from "./StepTenantSelector";
import StepRegistrationForm from "./StepRegistrationForm";
import StepProgress from "./StepProgress";
import * as UCJApi from "../constants/apiMethodsUCJ";

// ─── Step indicator ──────────────────────────────────────────────────────────

const STEP_LABELS = {
  tenant: "Select Tenant",
  form:   "Registration",
  progress: "Processing",
};

function StepIndicator({ steps, currentStep }) {
  return (
    <div className="ucj-step-indicator">
      {steps.map((step, idx) => {
        const currentIdx = steps.indexOf(currentStep);
        const state =
          idx < currentIdx ? "done" :
          idx === currentIdx ? "active" : "pending";
        return (
          <React.Fragment key={step}>
            <div className={`ucj-step ucj-step--${state}`}>
              <div className="ucj-step__dot">
                {state === "done" ? "✓" : idx + 1}
              </div>
              <span className="ucj-step__label">{STEP_LABELS[step]}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`ucj-step__line ucj-step__line--${state === "done" ? "done" : "pending"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Inner app (has access to context) ───────────────────────────────────────

function UCJApp() {
  const state    = useRegistration();
  const dispatch = useRegistrationDispatch();

  const isLoggedIn = localStorage.getItem("IsUserLoggedIn") === "true";
  const userEmail  = localStorage.getItem("email") || "";

  // Parse URL params and store in context
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const get = (key) => params.get([...params.keys()].find(k => k.toUpperCase() === key.toUpperCase()) || "") || "";
    dispatch({
      type: "SET_URL_PARAMS",
      payload: {
        productFamily: get("PRODUCTFAMILY"),
        licenseType:   get("LICTYPE"),
        pakId:         get("PAKID"),
        isBoxx:        get("PARTNER").toUpperCase() === "BOXX",
        partner:       get("PARTNER"),
      },
    });
  }, []);

  // Seed email from localStorage into form once we know it
  useEffect(() => {
    if (isLoggedIn && userEmail) {
      dispatch({ type: "SET_EMAIL", payload: userEmail });
    }
  }, [isLoggedIn, userEmail]);

  // Load initialize data (masterdata + rwamapping + ngcCreate) — no auth needed
  useEffect(() => {
    if (!state.productFamily && !state.licenseType) return; // wait for params
    axios
      .get(UCJApi.UCJ_INITIALIZE(), {
        params: {
          productFamily: state.productFamily,
          licenseType:   state.licenseType,
          isBoxx:        state.isBoxx,
        },
      })
      .then((res) => {
        const d = res.data;
        dispatch({
          type: "INITIALIZE_SUCCESS",
          payload: {
            masterData:        d.masterData,
            rwaMappings:       d.rwaMappings,
            ngcCreateRequired: d.ngcCreateRequired,
            ngcProductFamily:  d.ngcProductFamily,
          },
        });
      })
      .catch((err) => {
        dispatch({ type: "INITIALIZE_ERROR", payload: err.message });
      });
  }, [state.productFamily, state.licenseType, state.isBoxx]);

  // Once initialize is done, build the step list
  useEffect(() => {
    if (state.isInitializing) return;
    const steps = [];
    if (state.ngcCreateRequired) steps.push("tenant");
    steps.push("form");
    steps.push("progress");
    dispatch({ type: "SET_STEPS", payload: steps });
  }, [state.isInitializing, state.ngcCreateRequired]);

  // Load returning-user profile after login
  useEffect(() => {
    if (!isLoggedIn || !userEmail || state.isInitializing) return;
    dispatch({ type: "SET_LOADING_PROFILE", payload: true });
    axios
      .get(UCJApi.UCJ_USER_PROFILE(), { params: { email: userEmail } })
      .then((res) => {
        dispatch({ type: "USER_PROFILE_LOADED", payload: res.data });
      })
      .catch(() => {
        dispatch({ type: "USER_PROFILE_LOADED", payload: { exists: false } });
      });
  }, [isLoggedIn, userEmail, state.isInitializing]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (!isLoggedIn) {
    return <Start />;
  }

  if (state.isInitializing || state.steps.length === 0) {
    return (
      <div className="ucj-loading">
        <span className="ucj-spinner" /> Loading...
      </div>
    );
  }

  const renderStep = () => {
    switch (state.currentStep) {
      case "tenant":
        return <StepTenantSelector />;
      case "form":
        return <StepRegistrationForm />;
      case "progress":
        return <StepProgress />;
      default:
        return null;
    }
  };

  return (
    <div className="ucj-wrapper">
      <StepIndicator steps={state.steps} currentStep={state.currentStep} />
      <div className="ucj-step-content">
        {renderStep()}
      </div>
    </div>
  );
}

// ─── Root export (wraps with Provider) ───────────────────────────────────────

export default function AppUCJ() {
  return (
    <>
      <AppHeader />
      <RegistrationProvider>
        <UCJApp />
      </RegistrationProvider>
      <Footer />
    </>
  );
}
